import { useState } from 'react';
import { Typography, Box, Stack, Divider, Button, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';

/**
 * Reusable "how to allow the Traaker crawler through a WAF" instructions block.
 * Shared by the catalogue-create discovery flow (sitemap blocked) and the optimize
 * flow (pages blocked). We do NOT bypass WAFs — the honest ask is that the retailer's
 * IT allows our crawler.
 *
 * Two verification methods, ranked deliberately for a SERVERLESS crawler (Lambda):
 *  1. Signed header `X-Traaker-Verify: <per-tenant token>` — the recommended, defensible
 *     option. Generated/rotated here on demand (POST /tenants/waf-verify-token), stored
 *     on tenant.settings.wafVerifyToken, and sent on every discovery + optimize fetch.
 *  2. User-Agent contains `TraakerBot` — works with zero setup but is public/spoofable,
 *     so some security teams won't accept it alone.
 * IP allowlisting is explicitly discouraged: our egress is AWS's shared, rotating pool,
 * so there's no small stable IP list to hand out.
 */
export function WafWhitelistInstructions() {
  const { tenant, refreshProfile } = useAuth();
  const token = tenant?.settings?.wafVerifyToken;
  const [busy, setBusy] = useState(false);

  const rotate = async () => {
    setBusy(true);
    try {
      await api.post('/tenants/waf-verify-token', {});
      await refreshProfile();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      {/* Method 1 — signed header (recommended) */}
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Recommandé : autoriser par en-tête signé
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Traaker envoie un en-tête secret propre à votre compte sur chaque requête.
        Demandez à votre équipe technique d'autoriser (allow / skip) les requêtes
        portant cet en-tête — c'est plus fiable qu'un User-Agent (public et falsifiable).
      </Typography>

      {token ? (
        <Box
          sx={{
            fontFamily: 'monospace',
            fontSize: 13,
            bgcolor: 'action.hover',
            p: 1,
            borderRadius: 1,
            mb: 1,
            userSelect: 'all',
            wordBreak: 'break-all',
          }}
        >
          X-Traaker-Verify: {token}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
          Aucun en-tête généré pour l'instant.
        </Typography>
      )}
      <Button size="small" variant="outlined" onClick={rotate} disabled={busy} sx={{ mb: 2 }}>
        {busy ? (
          <CircularProgress size={16} />
        ) : token ? (
          "Régénérer l'en-tête"
        ) : (
          "Générer l'en-tête"
        )}
      </Button>
      {token && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Régénérer invalide l'ancien en-tête : prévenez votre équipe technique avant.
        </Typography>
      )}

      <Divider sx={{ mb: 1.5 }} />

      {/* Method 2 — User-Agent (zero setup) */}
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Alternative : autoriser par User-Agent
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Sans configuration côté Traaker, toutes nos requêtes portent un User-Agent
        contenant <code>TraakerBot</code>. Ajoutez une règle « autoriser » sur cette
        chaîne (moins sûr : un User-Agent peut être imité) :
      </Typography>
      <Box
        sx={{
          fontFamily: 'monospace',
          fontSize: 13,
          bgcolor: 'action.hover',
          p: 1,
          borderRadius: 1,
          mb: 2,
          userSelect: 'all',
        }}
      >
        Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like
        Gecko) Chrome/120.0.0.0 Safari/537.36 TraakerBot/1.0
      </Box>

      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Étapes par pare-feu
      </Typography>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="body2">
          • <strong>Cloudflare</strong> : Security → WAF → Custom rules → action{' '}
          « Skip » quand <code>http.request.headers["x-traaker-verify"]</code> (ou{' '}
          <code>http.user_agent contains "TraakerBot"</code>).
        </Typography>
        <Typography variant="body2">
          • <strong>Akamai</strong> : Bot Manager → Allowed bots, ou une règle WAF qui
          autorise l'en-tête <code>X-Traaker-Verify</code>.
        </Typography>
        <Typography variant="body2">
          • <strong>AWS WAF</strong> : règle Allow avec un match sur l'en-tête{' '}
          <code>X-Traaker-Verify</code> (priorité au-dessus des règles de blocage).
        </Typography>
        <Typography variant="body2">
          • <strong>DataDome</strong> : ajouter l'en-tête / le User-Agent TraakerBot à la
          custom allowlist.
        </Typography>
        <Typography variant="body2">
          • <strong>Imperva / Incapsula</strong> : Security → Bot Access Control →
          autoriser l'en-tête <code>X-Traaker-Verify</code>.
        </Typography>
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Et l'autorisation par adresse IP ?
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Traaker fonctionne en serverless (AWS Lambda) : nos requêtes proviennent d'un pool
        d'adresses AWS partagé et changeant, il n'existe donc pas de petite liste d'IP fixe
        à autoriser. Utilisez l'en-tête signé ci-dessus. Si votre politique impose une IP
        dédiée, contactez-nous — nous pouvons la mettre en place à la demande.
      </Typography>
    </Box>
  );
}
